import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Building2, Stethoscope, Users } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";

const SEGMENTS = [
  {
    id: "hospitals",
    icon: Building2,
    tab: "For Hospitals",
    title: "For Hospitals",
    desc: "Helping healthcare institutions explore opportunities in:",
    items: [
      "Hospital consultancy",
      "Insurance & TPA processes",
      "Cashless process support",
      "Hospital empanelment support",
      "Staff training",
      "Healthcare business development",
      "Professional networking",
    ],
    cta: "For Hospitals",
  },
  {
    id: "professionals",
    icon: Stethoscope,
    tab: "For Professionals",
    title: "For Healthcare Professionals",
    desc: "A platform for learning, networking and professional collaboration.",
    items: [
      "Healthcare knowledge",
      "Training initiatives",
      "Professional conversations",
      "Healthcare networking",
      "Industry insights",
    ],
    cta: "Connect With Us",
  },
  {
    id: "patients",
    icon: Users,
    tab: "For Patients & Public",
    title: "For Patients & General Public",
    desc: "Healthcare information designed to be simple, practical and easier to understand.",
    items: [
      "Health insurance awareness",
      "Cashless hospitalization awareness",
      "Healthcare documentation awareness",
      "General healthcare information",
      "Healthcare navigation information",
    ],
    cta: "Explore Healthcare Knowledge",
  },
];

export const Ecosystem = () => {
  const [active, setActive] = useState(0);
  const seg = SEGMENTS[active];

  return (
    <section id="ecosystem" className="relative bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionTag index="03" label="Our 360° Healthcare Ecosystem" />
          <h2 className="font-display mt-6 max-w-3xl text-2xl font-bold tracking-tight text-[#0B3C5C] sm:text-3xl lg:text-4xl">
            One Healthcare Platform. Multiple Possibilities.
          </h2>
          <p className="mt-5 max-w-2xl text-base text-[#4A6B82] sm:text-lg">
            We bring different healthcare needs and stakeholders together under one
            professional ecosystem.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-12 flex flex-wrap gap-3" role="tablist">
            {SEGMENTS.map((s, i) => (
              <button
                key={s.id}
                role="tab"
                aria-selected={active === i}
                data-testid={`ecosystem-tab-${s.id}`}
                onClick={() => setActive(i)}
                className={`flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition-all duration-300 ${
                  active === i
                    ? "border-[#5FA832] bg-[#5FA832] text-white shadow-lg shadow-[#5FA832]/25"
                    : "border-[#0B3C5C]/15 bg-[#0B3C5C]/[0.04] text-[#33586F] hover:border-teal-500/40 hover:text-[#5FA832]"
                }`}
              >
                <s.icon className="h-4 w-4" />
                {s.tab}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={seg.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 gap-10 rounded-[2rem] border border-[#0B3C5C]/10 bg-gradient-to-br from-white to-[#EAF3F8] p-8 shadow-lg shadow-[#0B3C5C]/5 sm:p-12 lg:grid-cols-2"
              data-testid={`ecosystem-panel-${seg.id}`}
            >
              <div>
                <span className="font-mono-x text-[10px] uppercase tracking-[0.3em] text-amber-500">
                  Segment {String(active + 1).padStart(2, "0")} / 03
                </span>
                <h3 className="font-display mt-4 text-xl font-bold text-[#0B3C5C] sm:text-2xl">{seg.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#4A6B82] sm:text-base">{seg.desc}</p>
                <a
                  href="#enquiry"
                  data-testid={`ecosystem-cta-${seg.id}`}
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F]"
                >
                  {seg.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
              <ul className="grid content-center gap-3">
                {seg.items.map((item, i) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.05, duration: 0.4 }}
                    className="flex items-center gap-3 rounded-xl border border-[#0B3C5C]/10 bg-[#0B3C5C]/[0.03] px-4 py-3 text-sm text-[#33586F]"
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rotate-45 bg-teal-400" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
